import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from 'victory';
import { RSIItem } from '../../../screens/ProfitCalculations/types';
import { useContext, useEffect, useMemo } from 'react';
import { MultiChartCursorContext, XAxisInfo } from '../../MultiChart';
import IndicatorLabel from '../IndicatorLabel';

interface Props {
  rsi?: RSIItem[];
  waver?: RSIItem[][];
  overExtensionThreshold?: number | null;
  label: string;

  width?: number | undefined;
  height?: number | undefined;
  xAxisInfo?: XAxisInfo | undefined;
  showXAxis?: boolean;
}

const RSIChart = (props: Props) => {
  const {
    width,
    height,
    label,
    overExtensionThreshold = 75,
    xAxisInfo,
    showXAxis,
  } = props;

  const data = useMemo(() => {
    return props.rsi;
  }, [props.rsi]);

  const waverData = useMemo(() => {
    return props.waver;
  }, [props.waver]);

  const dataPoints = useMemo(() => {
    if (!data || data?.length < 2) {
      return [];
    }

    /** Because the candles start at the beginning of the candle
     * and the rsi's value is al the end of the candle
     * We have to change position a little
     */
    const range =
      data[data.length - 1].timestamp - data[data.length - 2].timestamp;
    return data.map((d) => ({ x: d.timestamp - range, y: d.value })) ?? [];
  }, [data]);

  const waverDataDataSets = useMemo(() => {
    if (!waverData?.length || !waverData[0]?.length || !waverData[0]?.[0]) {
      return [];
    }

    /** Because the candles start at the beginning of the candle
     * and the rsi's value is at the end of the candle
     * We have to change position a little
     */
    const range = waverData[0][0].timestamp - waverData[0][1].timestamp;

    return waverData?.reduce((acc, curr) => {
      acc.push(curr.map((d) => ({ x: d.timestamp - range, y: d.value })));
      return acc;
    }, [] as Array<{ x: number; y: number }[]>);
  }, [waverData]);

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: '#999' },
    }),
    [],
  );

  const yTickValues = useMemo(() => {
    if (overExtensionThreshold) {
      return [0, 100 - overExtensionThreshold, 50, overExtensionThreshold, 100];
    }

    return [0, 25, 50, 75, 100];
  }, [overExtensionThreshold]);

  const overExtensionRangeDataPoints = useMemo(() => {
    if (!overExtensionThreshold || !xAxisInfo?.domain.x.length) {
      return [];
    }

    return [
      {
        x: xAxisInfo?.domain.x[0],
        y: overExtensionThreshold,
        y0: 100 - overExtensionThreshold,
      },
      {
        x: xAxisInfo?.domain.x[1],
        y: overExtensionThreshold,
        y0: 100 - overExtensionThreshold,
      },
    ];
  }, [overExtensionThreshold, xAxisInfo?.domain.x]);

  const gradientTopOffset = useMemo(() => {
    if (!overExtensionThreshold) {
      return 0;
    }

    const max = Math.max(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const min = Math.min(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const domainYRange = max - min;
    const topOffset = (max - overExtensionThreshold) / domainYRange;

    return topOffset;
  }, [dataPoints, overExtensionThreshold, waverDataDataSets]);

  const gradientBottomOffset = useMemo(() => {
    if (!overExtensionThreshold) {
      return 0;
    }

    const max = Math.max(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const min = Math.min(
      ...dataPoints.map((d) => d.y),
      ...waverDataDataSets.reduce(
        (acc, curr) => [...acc, ...curr.map((d) => d.y)],
        [] as number[],
      ),
    );
    const domainYRange = max - min;
    const topOffset = 1 - (100 - overExtensionThreshold - min) / domainYRange;

    return topOffset;
  }, [dataPoints, overExtensionThreshold, waverDataDataSets]);

  const xDomain = xAxisInfo?.domain?.x;

  const chartDataPoints = useMemo(() => {
    if (dataPoints.length < 2 || !xDomain) return dataPoints;
    const last = dataPoints[dataPoints.length - 1];

    const domainWidth = xDomain[1] - xDomain[0];
    const paddingPx = xAxisInfo?.domainPadding?.x ?? 0;
    const chartPaddingLeft = xAxisInfo?.chartPadding?.left ?? 0;
    const chartPaddingRight = xAxisInfo?.chartPadding?.right ?? 0;
    const netWidth =
      (width ?? 0) - chartPaddingLeft - chartPaddingRight - 2 * paddingPx;
    const rightPaddingInData =
      netWidth > 0 ? (paddingPx * domainWidth) / netWidth : 0;

    const extendTo = xDomain[1] + rightPaddingInData;
    if (last.x >= extendTo) return dataPoints;
    // Only bridge the small right-hand padding gap. If the series genuinely
    // ends well before the domain edge (e.g. MRC RSI, which only spans its
    // episodes) leave it truncated — never flat-line across a real gap.
    const spacing = last.x - dataPoints[dataPoints.length - 2].x;
    if (spacing > 0 && extendTo - last.x > spacing * 1.5) return dataPoints;
    return [...dataPoints, { x: extendTo, y: last.y }];
  }, [dataPoints, xDomain, xAxisInfo, width]);

  // Hover crosshair — the RSI point under the shared MultiChart cursor: a dot
  // on the line, and its value reported to the combined tooltip.
  const { cursorTimestamp, report } = useContext(MultiChartCursorContext);

  const shortLabel = useMemo(() => label.split(' · ')[0], [label]);

  const cursorPoint = useMemo(() => {
    if (cursorTimestamp == null || chartDataPoints.length === 0) {
      return null;
    }
    return chartDataPoints.reduce((best, point) =>
      Math.abs(point.x - cursorTimestamp) < Math.abs(best.x - cursorTimestamp)
        ? point
        : best,
    );
  }, [cursorTimestamp, chartDataPoints]);

  useEffect(() => {
    report(
      shortLabel,
      cursorPoint
        ? [{ label: shortLabel, value: cursorPoint.y.toFixed(1) }]
        : [],
    );
  }, [cursorPoint, shortLabel, report]);

  const chartWidth = width;
  const chartHeight = height || 120;
  const gradientId = `RSI_gradient_${Math.random()}`;

  return (
    <div
      className="candlesticks-container"
      style={{
        width: chartWidth,
        height: chartHeight,
      }}
    >
      <IndicatorLabel label={label} />
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0" stopColor="green" />
            <stop offset={gradientTopOffset} stopColor="gray" />
            <stop offset={gradientBottomOffset} stopColor="gray" />
            <stop offset="1" stopColor="red" />
          </linearGradient>
        </defs>
      </svg>
      <VictoryChart
        width={chartWidth}
        domain={{ x: xDomain ?? xAxisInfo?.domain.x, y: [-10, 105] }}
        domainPadding={xAxisInfo?.domainPadding}
        height={chartHeight}
        padding={{
          ...xAxisInfo?.chartPadding,
          top: 0,
          bottom: showXAxis ? 30 : 0,
        }}
      >
        <VictoryAxis
          tickFormat={showXAxis ? xAxisInfo?.tickFormat : () => ''}
          style={{ ...xAxisInfo?.axisStyle, ...(!showXAxis && { ticks: {} }) }}
          tickValues={xAxisInfo?.tickValues}
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={(t) => `${t}%`}
          orientation={'right'}
          tickLabelComponent={<VictoryLabel dx={5} />}
          tickValues={yTickValues}
        />

        {overExtensionRangeDataPoints && (
          <VictoryArea
            data={overExtensionRangeDataPoints}
            style={{
              data: {
                fill: '#FFC70020',
              },
            }}
          />
        )}

        <VictoryLine
          data={chartDataPoints}
          style={{
            data: { stroke: `url(#${gradientId})` },
          }}
        />
        {waverDataDataSets?.map((ds, index) => (
          <VictoryLine
            key={`waver_${index}`}
            data={ds}
            style={{
              data: { stroke: `url(#${gradientId})` },
            }}
          />
        ))}
        <VictoryScatter
          data={cursorPoint ? [cursorPoint] : []}
          size={3.5}
          style={{
            data: {
              fill: 'white',
              stroke: 'rgba(0,0,0,0.55)',
              strokeWidth: 1,
            },
          }}
        />
      </VictoryChart>
    </div>
  );
};

export default RSIChart;
